import React from 'react';
import { Box, Typography, Button, Chip } from '@mui/material';
import {
  Palette as PaletteIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  GetApp as ExportIcon,
} from '@mui/icons-material';
import { colors } from '../../../theme/designTokens';

interface AIStylesHeaderStats {
  activeCount: number;
  categoryCount: number;
  inGridCount: number;
  totalCount: number;
}

interface AIStylesHeaderProps {
  stats: AIStylesHeaderStats;
  onAddWriting: () => void;
  onAddIllustration: () => void;
  onRefresh: () => void;
  onExport: () => void;
}

const AIStylesHeader: React.FC<AIStylesHeaderProps> = ({
  stats,
  onAddWriting,
  onAddIllustration,
  onRefresh,
  onExport,
}) => {
  return (
    <Box
      sx={{
        bgcolor: colors.deepTeal[500],
        position: 'relative',
        overflow: 'hidden',
        borderRadius: '12px 12px 0 0',
        px: 3.5,
        pt: 3,
        pb: 2.5,
        display: 'flex',
        alignItems: 'stretch',
      }}
    >
      {/* Dot texture overlay */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          opacity: 0.06,
          backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)',
          backgroundSize: '18px 18px',
          pointerEvents: 'none',
        }}
      />

      {/* Left column: breadcrumb, title, subtitle, stats */}
      <Box sx={{ flex: 1, position: 'relative' }}>
        {/* Breadcrumb */}
        <Typography
          sx={{
            fontFamily: '"Montserrat", sans-serif',
            fontWeight: 500,
            fontSize: '0.72rem',
            color: 'rgba(255,255,255,0.4)',
            letterSpacing: '0.04em',
            mb: 1.5,
          }}
        >
          Admin Dashboard / AI & Styles / AI Styles
        </Typography>

        {/* Title row */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: '10px',
              bgcolor: 'rgba(255,255,255,0.12)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <PaletteIcon sx={{ color: '#fff', fontSize: 22 }} />
          </Box>
          <Typography
            sx={{
              fontFamily: '"Playfair Display", serif',
              fontWeight: 400,
              fontSize: '1.625rem',
              color: '#fff',
              letterSpacing: '-0.01em',
            }}
          >
            AI Styles
          </Typography>
        </Box>

        {/* Subtitle */}
        <Typography
          sx={{
            fontFamily: '"Montserrat", sans-serif',
            fontSize: '0.8rem',
            color: 'rgba(255,255,255,0.55)',
            mb: 2.5,
          }}
        >
          Manage styles for{' '}
          <Box component="span" sx={{ fontWeight: 700, color: 'rgba(255,255,255,0.85)' }}>
            Memory Artisan&trade;
          </Box>
          {' '}chapter creation &middot; Position <strong>#1</strong> = &ldquo;Most Popular&rdquo; badge only
          &mdash; new chapters always default to Your Own Words / Digital Art; members change theirs in app Settings
        </Typography>

        {/* Stats chips */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip
            size="small"
            label={`${stats.activeCount} Active`}
            sx={{
              fontFamily: '"Montserrat", sans-serif',
              fontWeight: 600,
              fontSize: '0.72rem',
              bgcolor: 'rgba(255,255,255,0.1)',
              color: '#fff',
              '& .MuiChip-label': { px: 1.2 },
            }}
            icon={
              <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: '#4ade80', ml: 0.8 }} />
            }
          />
          <Chip
            size="small"
            label={`${stats.categoryCount} Categories`}
            sx={{
              fontFamily: '"Montserrat", sans-serif',
              fontWeight: 600,
              fontSize: '0.72rem',
              bgcolor: 'rgba(255,255,255,0.1)',
              color: '#fff',
              '& .MuiChip-label': { px: 1.2 },
            }}
          />
          <Chip
            size="small"
            label={`${stats.inGridCount} In Grid`}
            sx={{
              fontFamily: '"Montserrat", sans-serif',
              fontWeight: 600,
              fontSize: '0.72rem',
              bgcolor: 'rgba(255,255,255,0.1)',
              color: '#fff',
              '& .MuiChip-label': { px: 1.2 },
            }}
            icon={
              <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: colors.warmGold[500], ml: 0.8 }} />
            }
          />
          <Chip
            size="small"
            label={`${stats.totalCount} Total`}
            sx={{
              fontFamily: '"Montserrat", sans-serif',
              fontWeight: 600,
              fontSize: '0.72rem',
              bgcolor: 'rgba(255,255,255,0.1)',
              color: '#fff',
              '& .MuiChip-label': { px: 1.2 },
            }}
          />
        </Box>
      </Box>

      {/* Right column: action buttons, vertically centered */}
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 1, position: 'relative', ml: 3 }}>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={onAddWriting}
          sx={{
            bgcolor: colors.warmGold[500],
            color: '#fff',
            fontFamily: '"Montserrat", sans-serif',
            fontWeight: 600,
            fontSize: '0.78rem',
            textTransform: 'none',
            borderRadius: '8px',
            px: 2,
            whiteSpace: 'nowrap',
            '&:hover': { bgcolor: colors.warmGold[600] },
          }}
        >
          Add Writing Style
        </Button>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={onAddIllustration}
          sx={{
            color: '#fff',
            borderColor: 'rgba(255,255,255,0.25)',
            fontFamily: '"Montserrat", sans-serif',
            fontWeight: 600,
            fontSize: '0.78rem',
            textTransform: 'none',
            borderRadius: '8px',
            px: 2,
            whiteSpace: 'nowrap',
            '&:hover': { borderColor: 'rgba(255,255,255,0.5)', bgcolor: 'rgba(255,255,255,0.05)' },
          }}
        >
          Add Illustration Style
        </Button>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={onRefresh}
            sx={{
              color: '#fff',
              borderColor: 'rgba(255,255,255,0.25)',
              fontFamily: '"Montserrat", sans-serif',
              fontWeight: 600,
              fontSize: '0.78rem',
              textTransform: 'none',
              borderRadius: '8px',
              flex: 1,
              whiteSpace: 'nowrap',
              '&:hover': { borderColor: 'rgba(255,255,255,0.5)', bgcolor: 'rgba(255,255,255,0.05)' },
            }}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ExportIcon />}
            onClick={onExport}
            sx={{
              color: '#fff',
              borderColor: 'rgba(255,255,255,0.25)',
              fontFamily: '"Montserrat", sans-serif',
              fontWeight: 600,
              fontSize: '0.78rem',
              textTransform: 'none',
              borderRadius: '8px',
              flex: 1,
              whiteSpace: 'nowrap',
              '&:hover': { borderColor: 'rgba(255,255,255,0.5)', bgcolor: 'rgba(255,255,255,0.05)' },
            }}
          >
            Export
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default AIStylesHeader;
